<template>
  <div class="main">
    <!-- Tab Navigation -->
    <h1 class="animated-name">Shawak Sharma</h1>
    <h2>Computer Scientist</h2>
    <h4><i>"The idea is to die young as late as possible"</i></h4>
    <div class="description">
      <p>
        Software engineer at McLaren Applied specialising in IoT, Linux-based
        networking, cloud computing, and containerised/embedded applications.
        Proficient in infrastructure-as-code and deployment automation using
        Bash and Python, driving innovation and delivering scalable solutions.
        With expertise in web and app development using React and Vue.js, I
        bring a keen eye for designing visually appealing software. As a hobby,
        I constantly explore the fascinating world of fintech, embracing ongoing
        learning to stay at the forefront of emerging technologies.
      </p>
      <p>
        Please <u><a href="mailto:sharmashawak@gmail.com">email</a></u> me for
        references or anything else.
      </p>

      <!-- Tab Navigation -->
      <div class="tabs">
        <button
          :class="{ active: activeTab === 'skills' }"
          @click="activeTab = 'skills'"
        >
          Skills
        </button>
        <button
          :class="{ active: activeTab === 'projects' }"
          @click="activeTab = 'projects'"
        >
          Projects
        </button>
        <button
          :class="{ active: activeTab === 'education' }"
          @click="activeTab = 'education'"
        >
          Education
        </button>
      </div>
      <!-- Skills Grid -->
      <div v-if="activeTab === 'skills'">
        <h1>Skills</h1>
        <div class="skills-grid">
          <!-- Programming Languages & Technologies -->
          <div class="skills-box programming">
            <h4>Programming Languages & Technologies</h4>
            <div class="skills-inner-grid">
              <div
                v-for="skill in programmingSkills"
                :key="skill.name"
                class="skill"
                :data-level="skill.level"
                :data-title="getProficiency(skill.level)"
              >
                <span>{{ skill.name }}</span>
                <div class="skill-bar"></div>
              </div>
            </div>
          </div>

          <!-- Cloud/IoT -->
          <div class="skills-box cloud">
            <h4>Cloud/IoT/AWS</h4>
            <div class="skills-inner-grid">
              <div
                v-for="skill in cloudSkills"
                :key="skill.name"
                class="skill"
                :data-level="skill.level"
                :data-title="getProficiency(skill.level)"
              >
                <span>{{ skill.name }}</span>
                <div class="skill-bar"></div>
              </div>
            </div>
          </div>

          <!-- Web Development -->
          <div class="skills-box web-dev">
            <h4>Web Development</h4>
            <div class="skills-inner-grid">
              <div
                v-for="skill in webDevSkills"
                :key="skill.name"
                class="skill"
                :data-level="skill.level"
                :data-title="getProficiency(skill.level)"
              >
                <span>{{ skill.name }}</span>
                <div class="skill-bar"></div>
              </div>
            </div>
          </div>

          <!-- Music and Sport -->
          <div class="skills-box music-sport">
            <h4>Music and Sport</h4>
            <div class="skills-inner-grid">
              <div
                v-for="skill in musicSportSkills"
                :key="skill.name"
                class="skill"
                :data-level="skill.level"
                :data-title="getProficiency(skill.level)"
              >
                <span>{{ skill.name }}</span>
                <div class="skill-bar"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Education Section -->
      <div v-else-if="activeTab === 'education'" class="education">
        <h1>Education</h1>

        <!-- Newcastle University Entry -->
        <div class="education-entry university">
          <h3>Newcastle University</h3>
          <h4>
            <i>(September 2017 – 2021)<br />Newcastle upon Tyne NE1 7RU</i>
          </h4>
          <p>
            <b>First Class BSc</b><br />Computer Science with a Year in Industry
          </p>
        </div>

        <!-- Bingley Grammar School Entry -->
        <div class="education-entry school">
          <h3>Bingley Grammar School</h3>
          <h4>
            <i
              >(September 2010 – July 2017)<br />Keighley Rd, Bingley, Yorkshire
              BD16 2RS</i
            >
          </h4>
          <p>
            <b>A-Levels</b><br />A-Cs in Maths, Further Maths and Physics,
            including an A in Maths. <br /><b>GCSEs</b><br />12 A*-Bs, including
            7 As and an A* in Mathematics.
          </p>
        </div>
      </div>

      <!-- Projects Section -->
      <div v-else-if="activeTab === 'projects'" class="projects">
        <h1>Projects</h1>

        <!-- Golden Goals Entry -->
        <div class="project-entry goals">
          <h3>
            Golden Goals (SwiftUI) - iOS Goal Tracking App (Coursework - 1st
            Class)
          </h3>
          <h4><i>(February 2021 – June 2021)</i></h4>
          <ul>
            <li>
              Explored elements of creating a successful mobile application,
              conducting research on user studies and human-computer interaction
              to build a user-friendly interface that simplifies interactivity
              and displays complex information in a concise manner.
            </li>
            <li>
              Used JSON to locally store, retrieve and parse application state,
              to include information in and navigate within the frontend.
            </li>
          </ul>
        </div>

        <!-- 2D Platform Entry -->
        <div class="project-entry platform">
          <h3>2D Platformer (C++) - Physics Engine (Coursework - 1st Class)</h3>
          <h4><i>(January 2021 – June 2021)</i></h4>
          <ul>
            <li>
              Emulated a physics engine. Learned to program collision
              resolutions between soft, rigid and different shaped bodies,
              implementing springs, drag and gravity, applying game rules and
              working with animations and textures.
            </li>
          </ul>
        </div>

        <!-- Workspot Entry -->
        <div class="project-entry workspot">
          <h3>
            Workspot (SwiftUI) - iOS App to find suitable working venues as an
            alternative for working from home (Dissertation - 1st Class)
          </h3>
          <h4><i>(October 2021 – June 2021)</i></h4>
          <ul>
            <li>
              Completed relevant academic research to determine that people may
              face distractions, workspace issues, and other disadvantages when
              working from home for long periods.
            </li>
            <li>
              Implemented an app that takes advantage of Google Places and Apple
              Maps developer tools to locate suitable working environments near
              a user and display rating, price rating, phone number, website and
              other useful details.
            </li>
            <li>
              Used software design techniques such as a moodboard, font board
              and storyboard to blueprint a structure and aesthetic, and
              tailored the app to a target audience using personas.
            </li>
            <li>
              Composed a plan of action, a timeline, and architecture diagrams
              with an optimal solution to ensure the project is well organised.
              Produced contingency plans: found substitute tools and
              technologies as a backup solution.
            </li>
            <li>
              Learned to interact with APIs in real-time, using live data to
              construct a dynamic frontend for the app with the user’s selected
              filters and location.
            </li>
          </ul>
        </div>

        <!-- Alien Planet Entry -->
        <div class="project-entry planet">
          <h3>
            Alien Planet (Unity) - 3D Game Map and Terrain Design (Coursework -
            1st Class)
          </h3>
          <h4><i>(September 2020 – January 2021)</i></h4>
          <ul>
            <li>
              Designed and created extraterrestrial terrain and environments
              using Unity. This project allowed me to explore my creativity in
              design and get familiar with 3D modelling tools.
            </li>
            <li>
              Developed C# scripts to create player-controlled camera movement,
              program shaders, creating orbiting and moving objects.
            </li>
            <li>
              Worked on optimising my implementation’s memory usage and frame
              rates while maintaining graphical fidelity.
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      activeTab: "skills", // Default tab
      programmingSkills: [
        { name: "Python", level: 3 },
        { name: "Java", level: 2 },
        { name: "C++", level: 2 },
        { name: "Bash", level: 2 },
        { name: "Linux", level: 3 },
        { name: "Swift", level: 1 },
        { name: "Unity", level: 1 },
        { name: "Git", level: 3 },
        { name: "Docker", level: 2 },
        { name: "Grafana", level: 2 }
      ].sort((a, b) => b.level - a.level),
      cloudSkills: [
        { name: "AWS Lambda", level: 3 },
        { name: "AWS DynamoDB (MySQL)", level: 3 },
        { name: "AWS S3", level: 3 },
        { name: "AWS CloudFront", level: 1 },
        { name: "AWS CodeCommit", level: 3 },
        { name: "AWS CloudFormation", level: 1 },
        { name: "AWS Greengrass", level: 3 },
        { name: "AWS Timestream", level: 3 },
        { name: "AWS IoT Core", level: 3 },
        { name: "AWS EC2", level: 3 },
        { name: "APIs", level: 2 },
        { name: "Network Protocols", level: 3 },
        { name: "Network Security", level: 2 },
        { name: "GitLab CI/CD", level: 3 }
      ].sort((a, b) => b.level - a.level),
      webDevSkills: [
        { name: "HTML/CSS", level: 3 },
        { name: "JavaScript", level: 1 },
        { name: "Vue.js", level: 1 },
        { name: "React", level: 1 },
        { name: "Bootstrap", level: 2 },
        { name: "Node.js", level: 1 }
      ].sort((a, b) => b.level - a.level),
      musicSportSkills: [
        { name: "Football", level: 3 },
        { name: "Swimming", level: 3 },
        { name: "Badminton", level: 2 },
        { name: "Squash", level: 2 },
        { name: "Tennis", level: 2 },
        { name: "Rugby", level: 2 },
        { name: "Guitar", level: 3 },
        { name: "Drums", level: 3 },
        { name: "Music Production", level: 1 }
      ].sort((a, b) => b.level - a.level)
    };
  },
  methods: {
    getProficiency(level) {
      switch (level) {
        case 1:
          return "Basic";
        case 2:
          return "Intermediate";
        case 3:
          return "Proficient";
        default:
          return "";
      }
    }
  }
};
</script>

<style scoped>
.skills-box {
  padding: 10px;
  border-radius: 10px;
  text-align: left;
}

.skills-inner-grid {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(120px, 1fr)
  ); /* Adjust the minmax value as needed */
  gap: 10px;
}

.skill {
  position: relative; /* To position the pseudo-elements correctly */
  z-index: 1; /* To ensure the text is above the pseudo-elements */
  background-color: rgba(255, 255, 255, 0.2); /* Base background color */
  cursor: pointer; /* To indicate it's interactive */
  color: #000000; /* White text color */
  display: flex; /* Flex layout for centering */
  align-items: center; /* Vertical centering */
  justify-content: center; /* Horizontal centering */
  padding: 7.5px;
}

.skill::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  z-index: -1; /* To ensure it's below the text */
  border-radius: 5px;
  transition: width 0.3s ease;
}

/* Set width based on data-level attribute */
.skill[data-level="1"]::before {
  width: 25%;
  background-color: #ffebaa; /* Color for level 1 */
}

.skill[data-level="2"]::before {
  width: 75%;
  background-color: #ffd700; /* Color for level 2 */
}

.skill[data-level="3"]::before {
  width: 100%;
  background-color: #ffc125; /* Color for level 3 */
}

/* Display proficiency level inside the skill box */

/* Display proficiency level inside the skill box */
.skill::after {
  content: attr(data-title); /* Get the content from data-title attribute */
  font-size: 12px; /* Adjust font size as needed */
  transition: opacity 0.3s ease; /* Smooth transition for the label */
  opacity: 0; /* Initially hidden */
  position: absolute; /* Absolute positioning */
  top: 0; /* Start from the top */
  left: 0; /* Start from the left */
  width: 100%; /* Take up the full width */
  height: 100%; /* Take up the full height */
  z-index: 2; /* Ensure it's above the skill bar */
  background-color: #ffc125; /* Semi-transparent black background */
  display: flex; /* Flex layout for centering */
  align-items: center; /* Vertical centering */
  justify-content: center; /* Horizontal centering */
  color: #000000; /* White text color */
  border-radius: 5px; /* Rounded corners */
}

.skill:hover::after {
  opacity: 1; /* Show the label on hover */
}

.programming {
  background-color: #2ab698; /* Gold */
}

.web-dev {
  background-color: #e88e6d; /* OrangeRed */
}

.cloud {
  background-color: #8a4faf; /* DarkTurquoise */
}

.music-sport {
  background-color: #3b648a; /* LimeGreen */
}

.skills-box h4 {
  font-size: 20px;
  margin-bottom: 15px;
}
.tabs {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

button {
  padding: 10px 20px;
  cursor: pointer;
  border: none;
  background-color: #eee;
  border-radius: 5px;
  transition: background-color 0.3s, color 0.3s;
}

button:hover {
  background-color: #ddd;
}

button.active {
  background-color: #2ab698; /* You can change this to any color you prefer */
  color: white;
}

.education {
  display: grid;
  grid-template-columns: 1fr; /* 2x1 grid */
  gap: 20px; /* Spacing between the entries */
}

.education h1,
h2,
h3,
h4 {
  color: #eee;
}

.education-entry {
  padding: 15px; /* Padding for each entry */
  border-radius: 10px; /* Rounded corners */
}

.projects {
  display: grid;
  grid-template-columns: 1fr; /* 2x1 grid */
  gap: 20px; /* Spacing between the entries */
}

.projects h1,
h2,
h3,
h4 {
  color: #eee;
}

.project-entry {
  padding: 15px; /* Padding for each entry */
  border-radius: 10px; /* Rounded corners */
}

/* Background colors for each entry */
.university {
  background-color: #2ab698; /* Light blue for university */
}

.school {
  background-color: #e88e6d; /* Light orange for school */
}
.planet {
  background-color: #2ab698; /* Light blue for university */
}

.workspot {
  background-color: #e88e6d; /* Light orange for school */
}
.platform {
  background-color: #2ab698; /* Light blue for university */
}

.goals {
  background-color: #e88e6d; /* Light orange for school */
}
</style>
